import { sentryCaptureError } from "@utils/sentry-utils";

/** Base used for hexadecimal conversion */
const HEX_BASE = 16;

/** Minimum length for each hex segment */
const HEX_PAD_LENGTH = 2;

/** Padding character for hex values */
const HEX_PAD_CHAR = "0";

/** Supported context types for image caching */
export type ImageContextType = "project" | "group";

/**
 * Manages image caching, compression, and retrieval.
 */
class ImageManager {
  /**
   * Builds a unique cache key for an image based on its source and context.
   *
   * @throws Will throw an error if the hash generation fails.
   */
  private async buildCacheKey(
    src: string,
    contextType: string,
    contextId: string
  ): Promise<string> {
    try {
      const hash = await crypto.subtle.digest(
        "SHA-256",
        new TextEncoder().encode(src)
      );
      const hashArray = Array.from(new Uint8Array(hash));
      const hashHex = hashArray
        .map((b) => b.toString(HEX_BASE).padStart(HEX_PAD_LENGTH, HEX_PAD_CHAR))
        .join("");
      return `https://${contextType}-${contextId}/${hashHex}`;
    } catch (error) {
      sentryCaptureError({ error, title: "Error generating cache key" });
      throw error;
    }
  }

  /**
   * Retrieves an image from cache or fetches it from the network.
   * An object containing the response and a flag indicating if compression should be skipped.
   */
  private async getCachedOrFetch(
    cacheKey: string,
    src: string,
    companyId: string
  ): Promise<{ response: Response; shouldSkipCompression: boolean }> {
    if (!("caches" in window)) {
      return { response: await fetch(src), shouldSkipCompression: true };
    }

    if (src.startsWith("/src/assets")) {
      return { response: await fetch(src), shouldSkipCompression: true };
    }

    const cache = await caches.open(`image-cache-${companyId}`);
    const cached = await cache.match(cacheKey);

    if (cached) {
      return { response: cached, shouldSkipCompression: true };
    }

    return { response: await fetch(src), shouldSkipCompression: false };
  }

  /**
   * Compresses an image blob using a Web Worker.
   *
   * Falls back to the original blob if compression fails.
   */
  private workerCompress(blob: Blob): Promise<Blob> {
    return new Promise((resolve) => {
      try {
        const worker = new Worker(
          new URL("../workers/image-compress-worker.ts", import.meta.url),
          { type: "module" }
        );
        worker.postMessage(blob);

        worker.onmessage = (e) => {
          const compressedBlob = e.data;

          if (compressedBlob.size === 0) {
            resolve(blob);
          } else {
            resolve(compressedBlob);
          }
        };
      } catch (error) {
        sentryCaptureError({ error, title: "Error initializing worker" });
        resolve(blob);
      }
    });
  }

  /** Removes old cache entries for a given context, keeping only the current key */
  private async cleanOldCacheEntries(
    cache: Cache,
    contextType: string,
    contextId: string,
    currentCacheKey: string
  ): Promise<void> {
    if (!("caches" in window)) {
      return;
    }

    try {
      const keys = await cache.keys();
      const prefix = `https://${contextType}-${contextId}/`;

      await Promise.all(
        keys.map(async (request) => {
          if (
            request.url.startsWith(prefix) &&
            request.url !== currentCacheKey
          ) {
            await cache.delete(request);
          }
        })
      );
    } catch (err) {
      sentryCaptureError({
        error: err,
        title: "Error cleaning old project images from cache",
      });
    }
  }

  /**
   * Retrieves an image as a Blob, compresses it if necessary, and caches it.
   * Returns an empty Blob if an error occurs.
   */
  async getImageBlob(
    src: string,
    companyId: string,
    contextType: string,
    contextId: string
  ): Promise<Blob> {
    try {
      const cacheKey = await this.buildCacheKey(src, contextType, contextId);

      const { response, shouldSkipCompression } = await this.getCachedOrFetch(
        cacheKey,
        src,
        companyId
      );
      const originalBlob = await response.blob();

      if (originalBlob.size === 0) {
        throw new Error("Image blob is empty");
      }

      if (shouldSkipCompression) {
        return originalBlob;
      }

      // Compress and store in cache
      const compressedBlob = await this.workerCompress(originalBlob);
      const cache = await caches.open(`image-cache-${companyId}`);

      // Clean old entries for this context
      await this.cleanOldCacheEntries(cache, contextType, contextId, cacheKey);

      try {
        await cache.put(cacheKey, new Response(compressedBlob));
      } catch (error) {
        sentryCaptureError({ error, title: "Error putting image in cache" });
      }

      return compressedBlob;
    } catch (error) {
      sentryCaptureError({ error, title: "Error getting image blob" });
      // fallback empty blob
      throw error;
    }
  }
}

export const imageManager = new ImageManager();
